import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  UserCheck, Clock, Phone, Mail, Calendar, Check, Loader2, RefreshCw, User, History, ChevronDown, ChevronUp, X, XCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PendingApprovalsTab = ({ token, onApproved }) => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [rejectedUsers, setRejectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [showHistory, setShowHistory] = useState(true);
  const [showRejected, setShowRejected] = useState(false);

  const fetchPendingUsers = async () => {
    try {
      const res = await axios.get(`${API}/mnro/users/pending-approval`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingUsers(res.data);
    } catch (error) {
      console.error("Error fetching pending users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedUsers = async () => {
    try {
      const res = await axios.get(`${API}/mnro/users/approved-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApprovedUsers(res.data);
    } catch (error) {
      console.error("Error fetching approved users:", error);
    }
  };

  const fetchRejectedUsers = async () => {
    try {
      const res = await axios.get(`${API}/mnro/users/rejected-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRejectedUsers(res.data);
    } catch (error) {
      console.error("Error fetching rejected users:", error);
    }
  };

  useEffect(() => {
    fetchPendingUsers();
    fetchApprovedUsers();
    fetchRejectedUsers();
    // Auto refresh every 10 seconds
    const interval = setInterval(() => {
      fetchPendingUsers();
      fetchApprovedUsers();
      fetchRejectedUsers();
    }, 10000);
    return () => clearInterval(interval);
  }, [token]);

  const handleApprove = async (userId) => {
    setApprovingId(userId);
    try {
      await axios.post(
        `${API}/mnro/users/${userId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Пользователь одобрен!");
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      fetchApprovedUsers(); // Refresh history
      if (onApproved) onApproved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка одобрения");
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (userId) => {
    setRejectingId(userId);
    try {
      await axios.post(
        `${API}/mnro/users/${userId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Регистрация отклонена");
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      fetchRejectedUsers(); // Refresh rejected history
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка отклонения");
    } finally {
      setRejectingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#EDEDED] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UserCheck className="text-[#EDEDED]" size={24} />
          <h2 className="text-xl font-bold text-white">
            Подтверждение регистрации
          </h2>
          {pendingUsers.length > 0 && (
            <span className="bg-[#FF8A1E] text-white text-sm font-bold px-2 py-0.5 rounded-full">
              {pendingUsers.length}
            </span>
          )}
        </div>
        <button
          onClick={() => { fetchPendingUsers(); fetchApprovedUsers(); }}
          className="p-2 bg-[#1A1A1A] rounded-lg hover:bg-[#1F1F1F] transition-colors"
          title="Обновить"
        >
          <RefreshCw size={18} className="text-[#A1A1AA]" />
        </button>
      </div>

      {/* Empty State for Pending */}
      {pendingUsers.length === 0 && (
        <div className="bg-[#1A1A1A] rounded-xl p-12 border border-[#1F1F1F] text-center">
          <Clock className="w-16 h-16 mx-auto text-[#A1A1AA] mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">
            Нет заявок на регистрацию
          </h3>
          <p className="text-[#A1A1AA]">
            Новые заявки появятся здесь автоматически
          </p>
        </div>
      )}

      {/* Pending Users List */}
      {pendingUsers.length > 0 && (
        <div className="grid gap-4">
          {pendingUsers.map((user) => (
            <div
              key={user.id}
              className="bg-[#1A1A1A] rounded-xl p-6 border border-[#1F1F1F] hover:border-[#2E2E2E]/30 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2E2E2E] to-[#1A1A1A] flex items-center justify-center text-white font-bold text-lg">
                    {user.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  
                  {/* User Info */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-[#A1A1AA]" />
                      <span className="text-white font-medium">{user.name || "Без имени"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-[#A1A1AA]" />
                      <span className="text-[#A1A1AA]">{user.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-[#A1A1AA]" />
                      <span className="text-[#EDEDED] font-medium">{user.phone || "Не указан"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-[#A1A1AA]" />
                      <span className="text-[#71717A] text-sm">{formatDate(user.created_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => handleApprove(user.id)}
                    disabled={approvingId === user.id}
                    className="px-6 py-3 bg-[#22C55E] text-white font-medium rounded-lg hover:bg-[#16A34A] transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {approvingId === user.id ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <Check size={18} />
                    )}
                    Одобрить
                  </button>
                  <button
                    onClick={() => handleReject(user.id)}
                    disabled={rejectingId === user.id}
                    className="px-4 py-3 bg-[#F87171]/15 text-[#F87171] font-medium rounded-lg hover:bg-[#F87171]/25 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {rejectingId === user.id ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <X size={18} />
                    )}
                    Отклонить
                  </button>
                </div>
              </div>

              {/* Status Badge */}
              <div className="mt-4 pt-4 border-t border-[#1F1F1F]">
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 text-[#EDEDED] rounded-full text-sm">
                  <Clock size={14} />
                  Ожидает подтверждения
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Instructions */}
      <div className="bg-[#0D3320] rounded-xl p-4 border border-[#22C55E]/30">
        <h4 className="text-[#22C55E] font-medium mb-2">💡 Подсказка</h4>
        <p className="text-[#A1A1AA] text-sm">
          После одобрения регистрации пользователь автоматически получит доступ к платформе.
          Рекомендуется сразу начислить начальный баланс и открыть первые сделки во вкладках
          «Пользователи» и «Сделки».
        </p>
      </div>

      {/* Approved Users History */}
      <div className="bg-[#1A1A1A] rounded-xl border border-[#1F1F1F]">
        {/* History Header */}
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full p-4 flex items-center justify-between hover:bg-[#1F1F1F]/50 transition-colors rounded-t-xl"
        >
          <div className="flex items-center gap-3">
            <History className="text-[#22C55E]" size={20} />
            <h3 className="text-lg font-semibold text-white">
              История одобренных заявок
            </h3>
            <span className="bg-[#22C55E]/20 text-[#22C55E] text-sm px-2 py-0.5 rounded-full">
              {approvedUsers.length}
            </span>
          </div>
          {showHistory ? (
            <ChevronUp className="text-[#A1A1AA]" size={20} />
          ) : (
            <ChevronDown className="text-[#A1A1AA]" size={20} />
          )}
        </button>

        {/* History Content */}
        {showHistory && (
          <div className="border-t border-[#1F1F1F]">
            {approvedUsers.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-[#A1A1AA]">История пуста</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#1F1F1F]">
                      <th className="text-left p-4 text-[#A1A1AA] font-medium text-sm">Имя</th>
                      <th className="text-left p-4 text-[#A1A1AA] font-medium text-sm">Email</th>
                      <th className="text-left p-4 text-[#A1A1AA] font-medium text-sm">Телефон</th>
                      <th className="text-left p-4 text-[#A1A1AA] font-medium text-sm">Дата регистрации</th>
                      <th className="text-left p-4 text-[#A1A1AA] font-medium text-sm">Дата одобрения</th>
                      <th className="text-left p-4 text-[#A1A1AA] font-medium text-sm">Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvedUsers.map((user) => (
                      <tr key={user.id} className="border-b border-[#1F1F1F]/50 hover:bg-[#1F1F1F]/30">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#22C55E]/20 flex items-center justify-center text-[#22C55E] font-bold text-sm">
                              {user.name?.charAt(0)?.toUpperCase() || "U"}
                            </div>
                            <span className="text-white">{user.name || "Без имени"}</span>
                          </div>
                        </td>
                        <td className="p-4 text-[#A1A1AA]">{user.email}</td>
                        <td className="p-4 text-[#EDEDED]">{user.phone || "-"}</td>
                        <td className="p-4 text-[#A1A1AA] text-sm">{formatDate(user.created_at)}</td>
                        <td className="p-4 text-[#A1A1AA] text-sm">{formatDate(user.approved_at)}</td>
                        <td className="p-4">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#22C55E]/10 text-[#22C55E] rounded text-xs">
                            <Check size={12} />
                            Одобрен
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rejected Users History */}
      <div className="bg-[#1A1A1A] rounded-xl border border-[#1F1F1F]">
        {/* Rejected Header */}
        <button
          onClick={() => setShowRejected(!showRejected)}
          className="w-full p-4 flex items-center justify-between hover:bg-[#1F1F1F]/50 transition-colors rounded-t-xl"
        >
          <div className="flex items-center gap-3">
            <XCircle className="text-[#F87171]" size={20} />
            <h3 className="text-lg font-semibold text-white">
              Отклонённые заявки
            </h3>
            <span className="bg-[#F87171]/20 text-[#F87171] text-sm px-2 py-0.5 rounded-full">
              {rejectedUsers.length}
            </span>
          </div>
          {showRejected ? (
            <ChevronUp className="text-[#A1A1AA]" size={20} />
          ) : (
            <ChevronDown className="text-[#A1A1AA]" size={20} />
          )}
        </button>

        {/* Rejected Content */}
        {showRejected && (
          <div className="border-t border-[#1F1F1F]">
            {rejectedUsers.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-[#A1A1AA]">Нет отклонённых заявок</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#1F1F1F]">
                      <th className="text-left p-4 text-[#A1A1AA] font-medium text-sm">Имя</th>
                      <th className="text-left p-4 text-[#A1A1AA] font-medium text-sm">Email</th>
                      <th className="text-left p-4 text-[#A1A1AA] font-medium text-sm">Телефон</th>
                      <th className="text-left p-4 text-[#A1A1AA] font-medium text-sm">Дата регистрации</th>
                      <th className="text-left p-4 text-[#A1A1AA] font-medium text-sm">Дата отклонения</th>
                      <th className="text-left p-4 text-[#A1A1AA] font-medium text-sm">Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rejectedUsers.map((user) => (
                      <tr key={user.id} className="border-b border-[#1F1F1F]/50 hover:bg-[#1F1F1F]/30">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#F87171]/20 flex items-center justify-center text-[#F87171] font-bold text-sm">
                              {user.name?.charAt(0)?.toUpperCase() || "U"}
                            </div>
                            <span className="text-white">{user.name || "Без имени"}</span>
                          </div>
                        </td>
                        <td className="p-4 text-[#A1A1AA]">{user.email}</td>
                        <td className="p-4 text-[#EDEDED]">{user.phone || "-"}</td>
                        <td className="p-4 text-[#A1A1AA] text-sm">{formatDate(user.created_at)}</td>
                        <td className="p-4 text-[#A1A1AA] text-sm">{formatDate(user.rejected_at)}</td>
                        <td className="p-4">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#F87171]/10 text-[#F87171] rounded text-xs">
                            <X size={12} />
                            Отклонён
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingApprovalsTab;
